import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { 
  Calendar, User, ArrowRight, BookOpen, Clock, 
  Tag, Search, Sparkles, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const pageRef = useRef(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/articles`)
      .then(res => res.json())
      .then(data => {
        setArticles(data);
        setLoading(false);
      })
      .catch(() => {
        // Fallback data
        setArticles([
          {
            id: '1',
            title: 'Unlocking New Opportunities with SAP S/4HANA',
            excerpt: 'Discover how SAP S/4HANA migration can transform your business operations and drive growth.',
            author: 'UZVI Services',
            created_at: new Date().toISOString(),
            image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
            tags: ['SAP', 'Digital Transformation'],
            slug: 'unlocking-opportunities-sap'
          },
          {
            id: '2',
            title: 'Cloud Migration Best Practices for Enterprises',
            excerpt: 'Learn the essential strategies for a successful cloud migration journey.',
            author: 'UZVI Services',
            created_at: new Date().toISOString(),
            image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80',
            tags: ['Cloud', 'Azure'],
            slug: 'cloud-migration-best-practices'
          },
          {
            id: '3',
            title: 'Transform Your Online Presence with Modern Web Development',
            excerpt: 'Explore the latest trends in web development and how they can elevate your business.',
            author: 'UZVI Services',
            created_at: new Date().toISOString(),
            image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
            tags: ['Web Development', 'Technology'],
            slug: 'transform-online-presence'
          },
        ]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [articles]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const filteredArticles = articles.filter(article => 
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const featuredArticle = filteredArticles[0];
  const otherArticles = filteredArticles.slice(1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-24 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-6">
                <BookOpen className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-sm font-medium text-gray-700">Insights & Updates</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-testid="blog-hero-title">
                Our
                <span className="bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x"> Blog</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-2xl mx-auto">
                Stay updated with the latest trends in technology, SAP solutions, and digital transformation.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 rounded-full border border-gray-200 bg-white shadow-lg shadow-gray-200/50 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all"
                  data-testid="blog-search-input"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Featured Article */}
        {featuredArticle && !loading && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="reveal-scale">
                <a 
                  href={`/blog/${featuredArticle.slug || featuredArticle.id}`}
                  className="block group"
                  data-testid="featured-article"
                >
                  <div className="grid lg:grid-cols-2 gap-8 items-center bg-gray-50 rounded-3xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <div className="relative h-64 lg:h-96 overflow-hidden">
                      <img 
                        src={featuredArticle.image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'}
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[#ff6b35] text-white text-xs font-semibold rounded-full">
                          Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-8 lg:p-12">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(featuredArticle.tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="px-3 py-1 bg-orange-50 text-[#ff6b35] text-xs font-medium rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 group-hover:text-[#ff6b35] transition-colors">
                        {featuredArticle.title}
                      </h2>
                      <p className="text-gray-600 mb-6 line-clamp-3">
                        {featuredArticle.excerpt || featuredArticle.content?.substring(0, 150)}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {featuredArticle.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(featuredArticle.created_at)}
                          </span>
                        </div>
                        <span className="inline-flex items-center gap-2 text-[#ff6b35] font-semibold group-hover:gap-3 transition-all">
                          Read More
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Articles Grid */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12 reveal">
              <h2 className="text-2xl font-bold text-gray-900">Latest Articles</h2>
              <span className="text-gray-500">{filteredArticles.length} articles</span>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-500">Try adjusting your search query</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
                {(otherArticles.length > 0 ? otherArticles : filteredArticles).map((article, index) => (
                  <a
                    key={article.id}
                    href={`/blog/${article.slug || article.id}`}
                    className="reveal-scale group"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`article-card-${article.id}`}
                  >
                    <div className="bento-card h-full flex flex-col overflow-hidden hover:border-[#ff6b35]/30">
                      <div className="relative h-48 -mx-8 -mt-8 mb-6 overflow-hidden">
                        <img 
                          src={article.image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(article.tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 bg-orange-50 text-[#ff6b35] text-xs font-medium rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#ff6b35] transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-grow">
                        {article.excerpt || article.content?.substring(0, 100)}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {formatDate(article.created_at)}
                        </div>
                        <span className="inline-flex items-center gap-1 text-[#ff6b35] font-medium text-sm group-hover:gap-2 transition-all">
                          Read
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-[#ff6b35] mx-auto mb-6 reveal" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 reveal" style={{ animationDelay: '0.1s' }}>
              Stay Updated
            </h2>
            <p className="text-gray-400 text-lg mb-10 reveal" style={{ animationDelay: '0.2s' }}>
              Get the latest insights on technology trends delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center reveal" style={{ animationDelay: '0.3s' }}>
              <input
                type="email"
                placeholder="Enter your email"
                className="px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all w-full sm:w-80"
                data-testid="newsletter-email"
              />
              <button 
                className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                data-testid="newsletter-subscribe"
              >
                Subscribe
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default BlogPage;
